import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPatient } from '../services/patientApi';
import { payWithRazorpay } from '../services/razorpayService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const DELIVERY_THRESHOLD = 500;
const DELIVERY_FEE       = 49;

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman & Nicobar Islands','Chandigarh','Delhi','Jammu & Kashmir',
  'Ladakh','Lakshadweep','Puducherry',
];

/* ── API helpers ──────────────────────────────────────────────────── */

// Upload prescription file → returns PrescriptionResponse
const uploadPrescription = async (token, tenantId, file, notes) => {
  const form = new FormData();
  form.append('file', file);
  if (notes) form.append('notes', notes);

  const res = await fetch(`${BASE_URL}/pharmacy/prescriptions`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID':   tenantId,
      // Note: do NOT set Content-Type here — browser sets multipart boundary automatically
    },
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Upload failed (${res.status})`);
  }
  return res.json(); // { id, fileName, fileType, status, uploadedAt, notes }
};


/* ── Field wrapper ────────────────────────────────────────────────── */
const Field = ({ label, required, error, children }) => (
  <div className="field">
    <label className="t-label">
      {label}{required && <span className="required-star">*</span>}
    </label>
    {children}
    {error && <div className="field-error">{error}</div>}
  </div>
);

const inputCls = (hasError) => `input co-input${hasError ? ' err' : ''}`;

/* ── RxUploadCard ─────────────────────────────────────────────────── */
// rxStatus values:
//   'idle'           — nothing uploaded
//   'uploading'      — POST in flight
//   'uploaded'       — successfully uploaded, user can proceed (verification happens in background)
//   'approved'       — backend status === APPROVED (used for pre-existing prescriptions)
//   'rejected'       — backend status === REJECTED (not currently used in new flow)
//   'error'          — network / server error during upload
// Stable constant outside the component — never needs to be in a useCallback dep array.
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const RxUploadCard = ({ rxStatus, setRxStatus, rxFile, setRxFile, uploadError, token, tenantId }) => {
  const fileInputRef  = useRef(null);
  const pollAbortRef  = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) { alert('Please upload a JPG, PNG, WebP, or PDF file.'); return; }
    if (file.size > 5 * 1024 * 1024)  { alert('File must be under 5 MB (backend limit).'); return; }

    setRxFile(file);
    setRxStatus('uploading');

    try {
      await uploadPrescription(token, tenantId, file, '');
    } catch (err) {
      setRxStatus('error');
      // surface error message via prop so parent can store it
      uploadError(err.message);
      return;
    }

    // File uploaded successfully - mark as uploaded and allow user to proceed
    // Verification will happen in the background (pharmacist will review later)
    setRxStatus('uploaded');
  }, [token, tenantId, setRxFile, setRxStatus, uploadError]);

  const reset = () => {
    if (pollAbortRef.current) pollAbortRef.current.abort();
    setRxFile(null);
    setRxStatus('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Cleanup poll on unmount
  useEffect(() => () => { if (pollAbortRef.current) pollAbortRef.current.abort(); }, []);

  if (rxStatus === 'idle') return (
    <div className={`rx-drop-zone${dragOver ? ' drag-over' : ''}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
      onClick={() => fileInputRef.current?.click()}>
      <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf"
        style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])}/>
      <div className="rx-idle-icon">℞</div>
      <div className="rx-idle-title">Upload your prescription</div>
      <div className="rx-idle-sub">Drag & drop or click to browse.<br/>JPG, PNG, WebP or PDF · Max 5 MB</div>
      <button className="btn-primary btn-sm-wide" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
        Choose file
      </button>
    </div>
  );

  if (rxStatus === 'uploading') return (
    <div className="rx-upload-box">
      <div className="rx-upload-icon">📤</div>
      <div className="rx-upload-title">Uploading…</div>
      <div className="t-meta">{rxFile?.name}</div>
    </div>
  );

  if (rxStatus === 'uploaded' || rxStatus === 'approved') return (
    <div className="rx-approved-box">
      <div className="avatar avatar-md avatar-success-circle">
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <div className="flex-1-min0">
        <div className="rx-approved-title">Prescription Uploaded ✓</div>
        <div className="rx-approved-file">{rxFile?.name}</div>
        {rxStatus === 'uploaded' && (
          <div className="t-meta" style={{ marginTop: 4, color: '#6366f1' }}>
            Your prescription will be verified by our pharmacist after order placement.
          </div>
        )}
      </div>
      <button className="rx-change-btn" onClick={reset}>Change</button>
    </div>
  );

  if (rxStatus === 'error') return (
    <div className="rx-rejected-box">
      <div className="rx-rejected-row">
        <div className="emoji-sm">❌</div>
        <div>
          <div className="rx-rejected-title">Upload failed</div>
          <div className="rx-rejected-sub">Could not reach the server. Please check your connection and try again.</div>
        </div>
      </div>
      <button className="btn-danger-sm" onClick={reset}>Try again</button>
    </div>
  );

  return null;
};

/* ── OrderSuccess ─────────────────────────────────────────────────── */
const OrderSuccess = ({ order, onContinue, onViewOrders }) => (
  <div className="order-success">
    <div className="order-success-circle">
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    </div>
    <h2 className="t-h2 order-success-title">Order Placed!</h2>
    <p className="t-body-sm order-success-sub">Your order has been confirmed.</p>
    <p className="order-id-text">
      Order ID: <strong className="order-id-em">{order.id}</strong>
    </p>

    {/* Order recap card */}
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
      padding: '20px 24px', marginBottom: 28, textAlign: 'left',
      boxShadow: '0 2px 12px rgba(79,70,229,0.07)',
    }}>
      {/* Items */}
      <div style={{ marginBottom: 16 }}>
        {order.items.map((item, i) => (
          <div key={item.productId} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 0',
            borderBottom: i < order.items.length - 1 ? '1px solid #f3f4f6' : 'none',
          }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1e1b4b', fontFamily: 'Sora, system-ui, sans-serif' }}>
                {item.productName}
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                Qty: {item.quantity} × ₹{parseFloat(item.unitPrice).toFixed(2)}
              </div>
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1e1b4b', fontFamily: 'DM Sans, sans-serif' }}>
              ₹{parseFloat(item.totalPrice).toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: '#6b7280' }}>
          <span>Payment</span>
          <span style={{ fontWeight: 600, color: '#374151' }}>{order.paymentMode}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#1e1b4b' }}>
          <span>Total</span>
          <span style={{ color: '#4f46e5' }}>₹{parseFloat(order.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Delivery address */}
      {order.deliveryAddress && (
        <div style={{
          marginTop: 14, paddingTop: 12, borderTop: '1px solid #f3f4f6',
          fontSize: 12, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>📦 Delivering to</div>
          {order.deliveryAddress}
        </div>
      )}
    </div>

    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
      <button className="btn-outline btn-auto" onClick={onViewOrders}>Track order</button>
      <button className="btn-primary btn-auto" onClick={onContinue}>Continue Shopping</button>
    </div>
  </div>
);

/* ── Checkout ─────────────────────────────────────────────────────── */
const Checkout = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();   // token must be exposed by AuthContext
  const { items, totalPrice, clearCart, removeFromCart } = useCart();
  const { tenant } = useTenant();

  const hasPrescriptionItem = items.some(i => i.prescription);

  useEffect(() => {
    if (!user) navigate('/login', { state: { from: '/checkout' } });
  }, [user, navigate]);

  // (Prescription status check removed — approval is handled server-side after order placement)

  const delivery   = totalPrice >= DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const orderTotal = totalPrice + delivery;
  const savings    = items.reduce((s, i) => s + (i.mrp - i.price) * i.quantity, 0);

  const [address, setAddress] = useState({
    fullName: user?.name || '', phone: user?.phone || '',
    line1: '', line2: '', city: '', state: '', pincode: '', type: 'Home',
  });

  // Derived: true only when all required fields pass validation rules (enables Place Order live)
  const isFormValid =
    address.fullName.trim() !== '' &&
    /^[6-9]\d{9}$/.test(address.phone) &&
    address.line1.trim() !== '' &&
    address.city.trim() !== '' &&
    address.state !== '' &&
    /^\d{6}$/.test(address.pincode);

  const [errors,      setErrors]      = useState({});
  const [payment,     setPayment]     = useState('cod');
  const [rxStatus,    setRxStatus]    = useState('idle');
  const [rxFile,      setRxFile]      = useState(null);
  const [rxUploadErr, setRxUploadErr] = useState('');
  const [placing,        setPlacing]        = useState(false);
  const [orderPlaced,    setOrderPlaced]    = useState(false);
  const [orderData,      setOrderData]      = useState(null);
  const [outOfStockItems, setOutOfStockItems] = useState([]); // product names flagged by backend

  // Redirect to cart if cart becomes empty (must be after all state declarations)
  useEffect(() => {
    if (user && items.length === 0 && !orderPlaced) navigate('/cart');
  }, [user, items.length, orderPlaced, navigate]);

  const set = (field) => (e) => setAddress(prev => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!address.fullName.trim()) e.fullName = 'Full name is required';
    if (!/^[6-9]\d{9}$/.test(address.phone)) e.phone = 'Enter a valid 10-digit mobile number';
    if (!address.line1.trim()) e.line1 = 'Address is required';
    if (!address.city.trim())  e.city  = 'City is required';
    if (!address.state)        e.state = 'Select a state';
    if (!/^\d{6}$/.test(address.pincode)) e.pincode = 'Enter a valid 6-digit pincode';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // User can proceed after uploading prescription (verification happens in background)
  const rxUploaded  = rxStatus === 'uploaded' || rxStatus === 'approved';
  const rxRequired  = hasPrescriptionItem;
  const canProceed  = rxRequired ? rxUploaded : true;

  const handlePlaceOrder = async () => {
    if (!validate() || !canProceed) return;
    setPlacing(true);
    setOutOfStockItems([]);
    try {
      const deliveryAddress = [
        address.fullName,
        address.line1,
        address.line2,
        address.city,
        address.state,
        address.pincode,
      ].filter(Boolean).join(', ');

      // ── NEW: pay via Razorpay first for UPI / Card ─────────────────────────
      // COD skips this entirely — payment happens on delivery as before.
      let paymentFields = {};
      if (payment !== 'cod') {
        try {
          const proof = await payWithRazorpay({
            amount: orderTotal,
            purpose: 'PHARMACY_ORDER',
            description: 'Pharmacy order',
            tenantName: tenant?.name,
            prefill: {
              name: address.fullName,
              contact: address.phone,
            },
          });
          paymentFields = {
            razorpayOrderId: proof.razorpayOrderId,
            razorpayPaymentId: proof.razorpayPaymentId,
            razorpaySignature: proof.razorpaySignature,
          };
        } catch (payErr) {
          setPlacing(false);
          alert(payErr.message || 'Payment was not completed.');
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────

      const res = await fetch(`${BASE_URL}/pharmacy/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': tenant.id,
        },
        body: JSON.stringify({
          items: items.map(i => ({ productId: i.id, quantity: i.quantity })),
          deliveryAddress,
          paymentMode: payment.toUpperCase(),
          customerName:  address.fullName,
          customerPhone: address.phone,
          ...paymentFields,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.message || `Order failed (${res.status})`;

        // Backend throws "Insufficient stock for product: <name>"
        const stockMatch = msg.match(/Insufficient stock for product:\s*(.+)/i);
        if (stockMatch) {
          const outOfStockProductName = stockMatch[1].trim();
          setOutOfStockItems([outOfStockProductName]);

          // Find and remove the out-of-stock product from cart
          const outOfStockItem = items.find(item => item.name === outOfStockProductName);
          if (outOfStockItem) {
            setTimeout(() => {
              // Auto-remove after 3 seconds so user can see the message
              removeFromCart(outOfStockItem.id);
              setOutOfStockItems([]);
            }, 3000);
          }
          return;
        }

        throw new Error(msg);
      }

      const data = await res.json();
      setOrderData(data);
      setOrderPlaced(true);
      clearCart();

      // Save name + phone for future autofill across all forms
      try {
        await createPatient({
          fullName: address.fullName,
          phone:    address.phone,
          isSelf:   true,
          relation: 'SELF',
          // address fields are not part of the patient schema; skipped
        });
      } catch {
        // Best-effort — never block the success screen
      }
    } catch (e) {
      setErrors(prev => ({ ...prev, _order: e.message }));
    } finally {
      setPlacing(false);
    }
  };

  if (!user) return null;

  if (orderPlaced) return (
    <div className="page">
      <main className="checkout-success-main">
        <OrderSuccess order={orderData} onContinue={() => navigate('/')} onViewOrders={() => navigate('/orders')} />
      </main>
    </div>
  );

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/cart')}>Cart</button>
          <span>›</span>
          <span className="bc-current">Checkout</span>
        </div>

        <h1 className="t-h1 fade-up co-h1">Checkout</h1>

        <div className="co-layout">

          {/* Left column */}
          <div className="co-main">

            {/* Address */}
            <div className="card fade-up-1 card-mb">
              <h2 className="t-h3">Delivery Address</h2>
              <div className="addr-type-row">
                {['Home', 'Work', 'Other'].map(t => (
                  <button key={t} className={`addr-chip${address.type === t ? ' active' : ''}`}
                    onClick={() => setAddress(a => ({ ...a, type: t }))}>{t}</button>
                ))}
              </div>
              <div className="grid-2col">
                <Field label="Full Name" required error={errors.fullName}>
                  <input className={inputCls(errors.fullName)} value={address.fullName} onChange={set('fullName')} placeholder="As on prescription"/>
                </Field>
                <Field label="Mobile Number" required error={errors.phone}>
                  <input className={inputCls(errors.phone)} value={address.phone} onChange={set('phone')} placeholder="10-digit number" maxLength={10} inputMode="numeric"/>
                </Field>
              </div>
              <Field label="Address Line 1" required error={errors.line1}>
                <input className={inputCls(errors.line1)} value={address.line1} onChange={set('line1')} placeholder="House / Flat no., Building, Street"/>
              </Field>
              <Field label="Address Line 2">
                <input className={inputCls(false)} value={address.line2} onChange={set('line2')} placeholder="Area, Colony, Landmark (optional)"/>
              </Field>
              <div className="grid-3col">
                <Field label="City" required error={errors.city}>
                  <input className={inputCls(errors.city)} value={address.city} onChange={set('city')} placeholder="e.g. Mumbai"/>
                </Field>
                <Field label="State" required error={errors.state}>
                  <select className={inputCls(errors.state)} value={address.state} onChange={set('state')} style={{ appearance: 'none', cursor: 'pointer' }}>
                    <option value="">Select state</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Pincode" required error={errors.pincode}>
                  <input className={inputCls(errors.pincode)} value={address.pincode} onChange={set('pincode')} placeholder="6-digit pincode" maxLength={6} inputMode="numeric"/>
                </Field>
              </div>
            </div>

            {/* Prescription — only shown when cart has Rx items */}
            {rxRequired && (
              <div className="card fade-up-2 card-mb rx-card-base"
                style={{ borderColor: rxUploaded ? '#6ee7b7' : rxStatus === 'error' ? '#fca5a5' : '#e0e7ff' }}>
                <div className="rx-card-header">
                  <div>
                    <h2 className="rx-card-title">
                      <span>℞</span> Prescription Upload
                      <span className="badge-required">Required</span>
                    </h2>
                    <p className="rx-card-sub">This order contains prescription medicine. A valid doctor's Rx is mandatory.</p>
                  </div>
                  <div className="strip-info">
                    <span>ℹ️</span>
                    <span className="rx-pharmacist">Verified by licensed pharmacist</span>
                  </div>
                </div>

                <details className="rx-details">
                  <summary className="rx-summary"><span>▾</span> What makes a valid prescription?</summary>
                  <ul className="rx-ul">
                    <li>Doctor's name, qualification &amp; registration number</li>
                    <li>Patient's name, age &amp; date of prescription</li>
                    <li>Medication name, dosage &amp; instructions</li>
                    <li>Doctor's signature or stamp</li>
                    <li>Prescription dated within the last 6 months</li>
                  </ul>
                </details>

                {rxUploadErr && (
                  <div className="field-error" style={{ marginBottom: '8px' }}>
                    ⚠️ {rxUploadErr}
                  </div>
                )}

                <RxUploadCard
                  rxStatus={rxStatus}
                  setRxStatus={setRxStatus}
                  rxFile={rxFile}
                  setRxFile={setRxFile}
                  uploadError={setRxUploadErr}
                  token={token}
                  tenantId={tenant?.id}
                />

                {!rxUploaded && rxStatus !== 'error' && (
                  <div className="strip-gate">
                    <span>🔒</span>
                    <span className="rx-pharmacist">You must upload your prescription before placing the order.</span>
                  </div>
                )}
              </div>
            )}

            {/* Payment */}
            <div className="card fade-up-3">
              <h2 className="t-h3">Payment Method</h2>
              {[
                { id: 'cod',  label: 'Cash on Delivery',    sub: 'Pay when your order arrives', icon: '💵', available: true },
                { id: 'upi',  label: 'UPI',                 sub: 'Pay securely via Razorpay',    icon: '📱', available: true },
                { id: 'card', label: 'Credit / Debit Card', sub: 'Pay securely via Razorpay',    icon: '💳', available: true },
              ].map(opt => (
                <div key={opt.id}
                  className={`payment-option${payment === opt.id ? ' active' : ''}${opt.available ? ' available' : ' unavailable'}`}
                  onClick={() => opt.available && setPayment(opt.id)}>
                  <span className="payment-icon">{opt.icon}</span>
                  <div className="flex-1-min0">
                    <div className="payment-label">{opt.label}</div>
                    <div className="payment-sub">{opt.sub}</div>
                  </div>
                  <div className={`payment-radio${payment === opt.id ? ' active' : ''}`}>
                    {payment === opt.id && <div className="payment-radio-dot"/>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: summary */}
          <div className="co-sidebar">
            <div className="card-sticky">
              <h2 className="t-h3">Order Summary</h2>

              <div className="sidebar-items">
                {items.map(item => (
                  <div key={item.id} className="sidebar-item">
                    <div className="emoji-box-sm">{item.emoji}</div>
                    <div className="sidebar-item-info">
                      <div className="sidebar-item-name">{item.name}</div>
                      <div className="sidebar-item-qty">Qty: {item.quantity}</div>
                    </div>
                    <div className="sidebar-item-price">₹{(item.price * item.quantity).toLocaleString()}</div>
                  </div>
                ))}
              </div>

              <div className="summary-row-sm">
                <span className="summary-label">Subtotal</span>
                <span className="summary-value">₹{totalPrice.toLocaleString()}</span>
              </div>
              <div className="summary-row-sm">
                <span className="summary-label">Delivery</span>
                <span className={delivery === 0 ? 'summary-free' : 'summary-value'}>
                  {delivery === 0 ? 'Free' : `₹${delivery}`}
                </span>
              </div>

              {savings > 0 && (
                <div className="strip-success strip-success-mb12">
                  <span>Your savings</span><span>−₹{savings.toLocaleString()}</span>
                </div>
              )}

              <div className="divider"/>

              <div className="summary-total">
                <span className="t-h4">Total</span>
                <span className="t-price-lg">₹{orderTotal.toLocaleString()}</span>
              </div>

              {/* Rx status badge — only when cart has Rx items */}
              {rxRequired && (
                <div className={`strip-rx-sidebar ${rxUploaded ? 'rx-status-approved' : 'rx-status-pending'}`}>
                  <span>{rxUploaded ? '✅' : '⚠️'}</span>
                  <span className={rxUploaded ? 'rx-status-text-approved' : 'rx-status-text-pending'}>
                    {rxUploaded ? 'Prescription uploaded' : 'Prescription required'}
                  </span>
                </div>
              )}

              {outOfStockItems.length > 0 && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fca5a5',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 10,
                  fontSize: 13, color: '#991b1b', fontFamily: 'DM Sans, sans-serif',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>❌ Out of stock</div>
                  {outOfStockItems.map(name => (
                    <div key={name} style={{ fontSize: 12, color: '#dc2626' }}>
                      <strong>{name}</strong> is no longer available. Removing from cart automatically...
                    </div>
                  ))}
                </div>
              )}

              {errors._order && (
                <div className="field-error" style={{ marginBottom: 8 }}>
                  ⚠️ {errors._order}
                </div>
              )}

              <button onClick={handlePlaceOrder} disabled={placing || !canProceed || !isFormValid || outOfStockItems.length > 0}
                className="btn-primary"
                style={{
                  background: (!canProceed || placing || !isFormValid) ? '#a5b4fc' : undefined,
                  cursor:     (!canProceed || placing || !isFormValid) ? 'not-allowed' : 'pointer',
                  boxShadow:  (!canProceed || placing || !isFormValid) ? 'none' : undefined,
                }}>
                {placing
                  ? 'Placing order…'
                  : (!canProceed && rxRequired)
                    ? '🔒 Upload Prescription First'
                    : 'Place Order →'}
              </button>

              <p className="tos-text">By placing your order you agree to our Terms of Service.</p>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Checkout;